import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddComment as AddCommentIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated, getCurrentUser } from '../../services/authService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * HIPAA/CUI Decisions - Shows decisions tracking for HIPAA/CUI compliance
 * @returns {JSX.Element} HipaaCuiDecisions component
 */
const HipaaCuiDecisions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [filteredDecisions, setFilteredDecisions] = useState([]);
  const [decisionFilters, setDecisionFilters] = useState({
    status: '',
    ownedBy: '',
    overdueFilter: '', // 'overdue', 'dueSoon', 'notOverdue', or '' for all
    searchText: '' // Quick search text
  });
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  
  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDecision, setViewingDecision] = useState(null);
  const [editingDecision, setEditingDecision] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    progressCommentsHistory: [],
    finalDecisionComment: '',
    status: 'Pending',
    ownedBy: '',
    dueDate: '',
    decisionDate: ''
  });
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    try {
      const user = getCurrentUser();
      setCurrentUser(user);
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  };

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await get('/api/hipaa-cui/decisions');
      
      if (response.success) {
        setDecisions(response.data || []);
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI decisions:', err);
      setError(err.message || 'Failed to load decisions data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const applyDecisionFilters = useCallback(() => {
    let filtered = [...decisions];

    // Default: exclude Completed when no status filter is selected
    if (!decisionFilters.status) {
      filtered = filtered.filter(d => d.status !== 'Completed');
    } else {
      filtered = filtered.filter(d => d.status === decisionFilters.status);
    }
    if (decisionFilters.ownedBy) {
      filtered = filtered.filter(d => d.ownedBy === decisionFilters.ownedBy);
    }
    if (decisionFilters.overdueFilter) {
      if (decisionFilters.overdueFilter === 'overdue') {
        filtered = filtered.filter(d => isOverdue(d.dueDate, d.status));
      } else if (decisionFilters.overdueFilter === 'dueSoon') {
        filtered = filtered.filter(d => isDueSoon(d.dueDate, d.status));
      } else if (decisionFilters.overdueFilter === 'notOverdue') {
        filtered = filtered.filter(d => !isOverdue(d.dueDate, d.status) && !isDueSoon(d.dueDate, d.status));
      }
    }

    // Quick search - search in title, description, ownedBy, and comments
    if (decisionFilters.searchText) {
      const searchLower = decisionFilters.searchText.toLowerCase().trim();
      filtered = filtered.filter(d => {
        const titleMatch = d.title?.toLowerCase().includes(searchLower) || false;
        const descriptionMatch = d.description?.toLowerCase().includes(searchLower) || false;
        const ownedByMatch = d.ownedBy?.toLowerCase().includes(searchLower) || false;
        const finalDecisionMatch = d.finalDecisionComment?.toLowerCase().includes(searchLower) || false;

        // Search in progress comments
        const commentsMatch = d.progressCommentsHistory?.some(comment =>
          comment.comment?.toLowerCase().includes(searchLower)
        ) || false;

        return titleMatch || descriptionMatch || ownedByMatch || finalDecisionMatch || commentsMatch;
      });
    }

    // Sort by status (default): use logical order Pending, In Progress, On Hold, Completed, Cancelled
    const statusOrder = ['Pending', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
    const statusRank = (s) => {
      const i = statusOrder.indexOf(s || '');
      return i >= 0 ? i : statusOrder.length;
    };
    if (sortBy === 'status') {
      filtered.sort((a, b) => {
        const rankA = statusRank(a.status);
        const rankB = statusRank(b.status);
        if (rankA !== rankB) {
          return sortOrder === 'asc' ? rankA - rankB : rankB - rankA;
        }
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateB - dateA;
      });
    } else {
      filtered.sort((a, b) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    setFilteredDecisions(filtered);
  }, [decisions, decisionFilters, sortBy, sortOrder]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyDecisionFilters();
  }, [applyDecisionFilters]);

  const handleOpenDialog = (decision = null) => {
    if (decision) {
      setEditingDecision(decision);
      setFormData({
        title: decision.title || '',
        description: decision.description || '',
        progressCommentsHistory: decision.progressCommentsHistory || [],
        finalDecisionComment: decision.finalDecisionComment || '',
        status: decision.status || 'Pending',
        ownedBy: decision.ownedBy || '',
        dueDate: decision.dueDate ? format(new Date(decision.dueDate), 'yyyy-MM-dd') : '',
        decisionDate: decision.decisionDate ? format(new Date(decision.decisionDate), 'yyyy-MM-dd') : ''
      });
    } else {
      setEditingDecision(null);
      setFormData({
        title: '',
        description: '',
        progressCommentsHistory: [],
        finalDecisionComment: '',
        status: 'Pending',
        ownedBy: '',
        dueDate: '',
        decisionDate: ''
      });
    }
    setNewComment('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDecision(null);
    setFormData({
      title: '',
      description: '',
      progressCommentsHistory: [],
      finalDecisionComment: '',
      status: 'Pending',
      ownedBy: '',
      dueDate: '',
      decisionDate: ''
    });
    setNewComment('');
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    const comment = {
      comment: newComment.trim(),
      addedAt: new Date().toISOString(),
      addedBy: currentUser?._id || null
    };
    
    setFormData({
      ...formData,
      progressCommentsHistory: [...formData.progressCommentsHistory, comment]
    });
    setNewComment('');
  };

  const handleRemoveComment = (index) => {
    const updatedComments = formData.progressCommentsHistory.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      progressCommentsHistory: updatedComments
    });
  };

  const handleSaveDecision = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.title.trim()) {
        setError('Title is required');
        setSaving(false);
        return;
      }

      // Prepare data with dueDate and decisionDate
      const dataToSave = {
        ...formData,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
        decisionDate: formData.decisionDate ? new Date(formData.decisionDate).toISOString() : null
      };

      let response;
      if (editingDecision) {
        response = await authPut(`/api/hipaa-cui/decisions/${editingDecision._id}`, dataToSave);
      } else {
        response = await authPost('/api/hipaa-cui/decisions', dataToSave);
      }

      if (response.success) {
        handleCloseDialog();
        await loadData(false);
        setSuccessMessage(editingDecision ? 'Decision updated successfully' : 'Decision created successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(response.message || 'Failed to save decision');
      }
    } catch (err) {
      console.error('Error saving decision:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save decision');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDecision = async (id) => {
    if (!window.confirm('Are you sure you want to delete this decision?')) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      const response = await authDelete(`/api/hipaa-cui/decisions/${id}`);

      if (response.success) {
        await loadData(false);
        setSuccessMessage('Decision deleted successfully');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError(response.message || 'Failed to delete decision');
      }
    } catch (err) {
      console.error('Error deleting decision:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete decision');
    } finally {
      setDeleting(null);
    }
  };

  const handleSortByStatus = () => {
    setSortBy('status');
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  // Get unique values for filters
  const uniqueStatuses = [...new Set(decisions.map(d => d.status).filter(Boolean))].sort();
  const uniqueOwners = [...new Set(decisions.map(d => d.ownedBy).filter(Boolean))].sort();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'On Hold':
        return 'warning';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  // Check if decision is overdue
  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Completed' || status === 'Cancelled') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  };

  // Check if decision is due soon (within 7 days)
  const isDueSoon = (dueDate, status) => {
    if (!dueDate || status === 'Completed' || status === 'Cancelled') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Get latest comment from progress comments history
  const getLatestComment = (progressCommentsHistory) => {
    if (!progressCommentsHistory || progressCommentsHistory.length === 0) {
      return '';
    }
    // Sort by addedAt to get the latest comment
    const sortedComments = [...progressCommentsHistory].sort((a, b) => {
      const dateA = a.addedAt ? new Date(a.addedAt) : new Date(0);
      const dateB = b.addedAt ? new Date(b.addedAt) : new Date(0);
      return dateB - dateA;
    });
    return sortedComments[0]?.comment || '';
  };

  // Truncate text for display
  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Escape HTML entities
  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Get status badge class for HTML
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'badge-success';
      case 'In Progress':
        return 'badge-info';
      case 'On Hold':
        return 'badge-warning';
      case 'Cancelled':
        return 'badge-error';
      default:
        return 'badge-default';
    }
  };

  // Download decision summary report as HTML/PDF
  const handleDownloadReport = () => {
    try {
      if (filteredDecisions.length === 0) {
        setError('No decisions to export');
        return;
      }

      const printWindow = window.open('', '_blank');
      const printDate = new Date();
      const formattedDate = format(printDate, 'MMMM dd, yyyy');
      const formattedTime = format(printDate, 'hh:mm a');
      
      // Calculate statistics
      const totalDecisions = filteredDecisions.length;
      const completedCount = filteredDecisions.filter(d => d.status === 'Completed').length;
      const inProgressCount = filteredDecisions.filter(d => d.status === 'In Progress').length;
      const pendingCount = filteredDecisions.filter(d => d.status === 'Pending').length;
      const onHoldCount = filteredDecisions.filter(d => d.status === 'On Hold').length;
      const cancelledCount = filteredDecisions.filter(d => d.status === 'Cancelled').length;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const overdueCount = filteredDecisions.filter(d => {
        if (!d.dueDate || d.status === 'Completed' || d.status === 'Cancelled') return false;
        const due = new Date(d.dueDate);
        due.setHours(0, 0, 0, 0);
        return due < today;
      }).length;
      const dueSoonCount = filteredDecisions.filter(d => {
        if (!d.dueDate || d.status === 'Completed' || d.status === 'Cancelled') return false;
        const due = new Date(d.dueDate);
        due.setHours(0, 0, 0, 0);
        const diffTime = due - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 && diffDays <= 7;
      }).length;
      
      const completionRate = totalDecisions > 0 ? ((completedCount / totalDecisions) * 100).toFixed(1) : 0;
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>HIPAA/CUI Decision Summary Report</title>
          <meta charset="UTF-8">
          <style>
            @page {
              margin: 2cm 1.5cm;
              size: A4;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .page-break {
                page-break-before: always;
              }
              thead {
                display: table-header-group;
              }
              tfoot {
                display: table-footer-group;
              }
              tr {
                page-break-inside: avoid;
              }
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: 'Calibri', 'Arial', 'Helvetica', sans-serif;
              font-size: 11pt;
              color: #2c3e50;
              line-height: 1.5;
              margin: 0;
              padding: 0;
              background: #ffffff;
            }
            .cover-page {
              min-height: 100vh;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              padding: 40px;
              border-bottom: 5px solid #1D4434;
            }
            .cover-title {
              font-size: 32pt;
              font-weight: 700;
              color: #1D4434;
              margin-bottom: 20px;
              letter-spacing: 1px;
            }
            .cover-subtitle {
              font-size: 18pt;
              color: #555;
              margin-bottom: 40px;
              font-weight: 300;
            }
            .cover-meta {
              margin-top: 60px;
              font-size: 12pt;
              color: #777;
            }
            .header {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
              padding: 25px 30px;
              margin-bottom: 30px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .header-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
            }
            .header-title {
              font-size: 24pt;
              font-weight: 700;
              margin: 0 0 8px 0;
              letter-spacing: 0.5px;
            }
            .header-subtitle {
              font-size: 11pt;
              opacity: 0.9;
              margin: 0;
            }
            .header-meta {
              text-align: right;
              font-size: 10pt;
              opacity: 0.9;
            }
            .executive-summary {
              background: #f8f9fa;
              border: 2px solid #1D4434;
              border-radius: 8px;
              padding: 25px;
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .executive-summary h2 {
              color: #1D4434;
              font-size: 18pt;
              font-weight: 700;
              margin: 0 0 20px 0;
              padding-bottom: 10px;
              border-bottom: 2px solid #1D4434;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-card {
              background: white;
              border-left: 4px solid #1D4434;
              padding: 15px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            .stat-label {
              font-size: 10pt;
              color: #666;
              margin-bottom: 8px;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .stat-value {
              font-size: 24pt;
              font-weight: 700;
              color: #1D4434;
            }
            .stat-card.warning .stat-value {
              color: #ed6c02;
            }
            .stat-card.error .stat-value {
              color: #d32f2f;
            }
            .stat-card.success .stat-value {
              color: #2e7d32;
            }
            .progress-section {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .progress-bar-container {
              background: #e0e0e0;
              border-radius: 10px;
              height: 25px;
              margin: 10px 0;
              overflow: hidden;
            }
            .progress-bar {
              background: linear-gradient(90deg, #2e7d32 0%, #4caf50 100%);
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 10pt;
              transition: width 0.3s ease;
            }
            .section-title {
              font-size: 16pt;
              font-weight: 700;
              color: #1D4434;
              margin: 30px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #1D4434;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              page-break-inside: auto;
            }
            thead {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
            }
            th {
              padding: 14px 10px;
              text-align: left;
              font-weight: 600;
              font-size: 10pt;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid rgba(255,255,255,0.2);
            }
            td {
              padding: 12px 10px;
              border: 1px solid #e0e0e0;
              vertical-align: top;
              word-wrap: break-word;
              font-size: 10pt;
            }
            tbody tr {
              transition: background-color 0.2s;
            }
            tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            tbody tr:hover {
              background-color: #e8f5e9;
            }
            .badge {
              display: inline-block;
              padding: 5px 12px;
              border-radius: 15px;
              font-size: 9pt;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              white-space: nowrap;
            }
            .badge-success { 
              background-color: #2e7d32; 
              color: white; 
            }
            .badge-info { 
              background-color: #0288d1; 
              color: white; 
            }
            .badge-warning { 
              background-color: #ed6c02; 
              color: white; 
            }
            .badge-error { 
              background-color: #d32f2f; 
              color: white; 
            }
            .badge-default { 
              background-color: #757575; 
              color: white; 
            }
            .comment-cell {
              max-width: 300px;
              word-wrap: break-word;
              white-space: pre-wrap;
              line-height: 1.4;
            }
            .title-cell {
              font-weight: 600;
              color: #1D4434;
            }
            .description-cell {
              max-width: 300px;
              word-wrap: break-word;
              line-height: 1.4;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #1D4434;
              text-align: center;
              font-size: 9pt;
              color: #666;
            }
            .overdue-row {
              background-color: rgba(211, 47, 47, 0.08) !important;
            }
            .due-soon-row {
              background-color: rgba(237, 108, 2, 0.08) !important;
            }
            .text-center {
              text-align: center;
            }
            .text-right {
              text-align: right;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-content">
              <div>
                <div class="header-title">HIPAA/CUI Decision Summary Report</div>
                <div class="header-subtitle">Compliance Management & Decision Tracking</div>
              </div>
              <div class="header-meta">
                <div><strong>Report Date:</strong> ${formattedDate}</div>
                <div><strong>Generated:</strong> ${formattedTime}</div>
              </div>
            </div>
          </div>

          <!-- Executive Summary -->
          <div class="executive-summary">
            <h2>Executive Summary</h2>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">Total Decisions</div>
                <div class="stat-value">${totalDecisions}</div>
              </div>
              <div class="stat-card success">
                <div class="stat-label">Completed</div>
                <div class="stat-value">${completedCount}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">In Progress</div>
                <div class="stat-value">${inProgressCount}</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Pending</div>
                <div class="stat-value">${pendingCount}</div>
              </div>
              ${onHoldCount > 0 ? `
              <div class="stat-card">
                <div class="stat-label">On Hold</div>
                <div class="stat-value">${onHoldCount}</div>
              </div>
              ` : ''}
              ${cancelledCount > 0 ? `
              <div class="stat-card">
                <div class="stat-label">Cancelled</div>
                <div class="stat-value">${cancelledCount}</div>
              </div>
              ` : ''}
              ${overdueCount > 0 ? `
              <div class="stat-card error">
                <div class="stat-label">Overdue</div>
                <div class="stat-value">${overdueCount}</div>
              </div>
              ` : ''}
              ${dueSoonCount > 0 ? `
              <div class="stat-card warning">
                <div class="stat-label">Due Soon (≤7 days)</div>
                <div class="stat-value">${dueSoonCount}</div>
              </div>
              ` : ''}
            </div>
            <div class="progress-section">
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span style="font-weight: 600; color: #1D4434;">Completion Rate</span>
                <span style="font-weight: 600; color: #1D4434;">${completionRate}%</span>
              </div>
              <div class="progress-bar-container">
                <div class="progress-bar" style="width: ${completionRate}%;">${completionRate}%</div>
              </div>
            </div>
          </div>

          <!-- Detailed Decision List -->
          <div class="section-title">Decision Details</div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 5%;" class="text-center">#</th>
                <th style="width: 18%;">Title</th>
                <th style="width: 20%;">Description</th>
                <th style="width: 10%;">Status</th>
                <th style="width: 10%;">Owned By</th>
                <th style="width: 12%;">Due Date</th>
                <th style="width: 12%;">Latest Comment</th>
                <th style="width: 13%;">Final Decision</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      filteredDecisions.forEach((decision, index) => {
        const latestComment = getLatestComment(decision.progressCommentsHistory);
        const statusBadgeClass = getStatusBadgeClass(decision.status || 'Pending');
        
        const decisionDueDate = decision.dueDate ? format(new Date(decision.dueDate), 'MMM dd, yyyy') : '-';
        const isOverdueReport = decision.dueDate && decision.status !== 'Completed' && decision.status !== 'Cancelled' && new Date(decision.dueDate) < new Date();
        const isDueSoonReport = decision.dueDate && decision.status !== 'Completed' && decision.status !== 'Cancelled' && (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const due = new Date(decision.dueDate);
          due.setHours(0, 0, 0, 0);
          const diffTime = due - today;
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays >= 0 && diffDays <= 7;
        })();
        
        const rowClass = isOverdueReport ? 'overdue-row' : isDueSoonReport ? 'due-soon-row' : '';
        const dueDateDisplay = decision.dueDate 
          ? `<span class="badge ${isOverdueReport ? 'badge-error' : isDueSoonReport ? 'badge-warning' : 'badge-default'}">${decisionDueDate}</span>`
          : '<span style="color: #999;">-</span>';
        
        htmlContent += `
          <tr class="${rowClass}">
            <td class="text-center">${index + 1}</td>
            <td class="title-cell">${escapeHtml(decision.title || '-')}</td>
            <td class="description-cell">${escapeHtml(decision.description || '-')}</td>
            <td><span class="badge ${statusBadgeClass}">${escapeHtml(decision.status || 'Pending')}</span></td>
            <td>${escapeHtml(decision.ownedBy || '-')}</td>
            <td>${dueDateDisplay}</td>
            <td class="comment-cell">${escapeHtml(latestComment || '-')}</td>
            <td class="comment-cell">${escapeHtml(decision.finalDecisionComment || '-')}</td>
          </tr>
        `;
      });
      
      htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            <div><strong>HIPAA/CUI Decision Summary Report</strong></div>
            <div style="margin-top: 5px;">Generated on ${formattedDate} at ${formattedTime}</div>
            <div style="margin-top: 5px;">This report contains ${totalDecisions} decision${totalDecisions !== 1 ? 's' : ''} | Confidential Document</div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      setTimeout(() => {
        printWindow.print();
        // Optionally close the window after printing (user can cancel if needed)
        // printWindow.close();
      }, 250);

      setSuccessMessage('Decision summary report opened for printing/download as PDF');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error generating decision report:', err);
      setError('Failed to generate decision report');
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Quick search..."
              value={decisionFilters.searchText}
              onChange={(e) => setDecisionFilters({ ...decisionFilters, searchText: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={decisionFilters.status}
                onChange={(e) => setDecisionFilters({ ...decisionFilters, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="">All (excl. Completed)</MenuItem>
                {uniqueStatuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Owned By</InputLabel>
              <Select
                value={decisionFilters.ownedBy}
                onChange={(e) => setDecisionFilters({ ...decisionFilters, ownedBy: e.target.value })}
                label="Owned By"
              >
                <MenuItem value="">All</MenuItem>
                {uniqueOwners.map(owner => (
                  <MenuItem key={owner} value={owner}>{owner}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Due Date</InputLabel>
              <Select
                value={decisionFilters.overdueFilter}
                onChange={(e) => setDecisionFilters({ ...decisionFilters, overdueFilter: e.target.value })}
                label="Due Date"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="dueSoon">Due Soon (≤7 days)</MenuItem>
                <MenuItem value="notOverdue">Not Overdue</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {isAuthenticated() && (
            <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.75 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
                disabled={filteredDecisions.length === 0}
              >
                Download Report
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{
                  backgroundColor: darkGreenColor,
                  '&:hover': { backgroundColor: '#0f2e1f' }
                }}
              >
                Add
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Decisions Table */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flex: 1 }}>
          <Box textAlign="center">
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading decisions...
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '& .MuiTable-root': {
              minWidth: 650
            }
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2,
                    '&:first-of-type': {
                      borderTopLeftRadius: '8px'
                    }
                  }}
                  width="80"
                >
                  Sr. No
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="25%"
                >
                  Title
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="25%"
                >
                  Progress Comments
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="10%"
                  sortDirection={sortBy === 'status' ? sortOrder : false}
                >
                  <TableSortLabel
                    active={sortBy === 'status'}
                    direction={sortBy === 'status' ? sortOrder : 'asc'}
                    onClick={handleSortByStatus}
                    sx={{ color: 'white', '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="10%"
                >
                  Owned By
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="12%"
                >
                  Due Date
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                  width="12%"
                >
                  Changed Date
                </TableCell>
                {isAuthenticated() && (
                  <TableCell 
                    align="right"
                    width="120"
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2,
                      '&:last-of-type': {
                        borderTopRightRadius: '8px'
                      }
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDecisions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAuthenticated() ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      No decisions found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDecisions.map((decision, index) => {
                  const overdue = isOverdue(decision.dueDate, decision.status);
                  const dueSoon = isDueSoon(decision.dueDate, decision.status);
                  
                  return (
                  <TableRow 
                    key={decision._id}
                    sx={{
                      backgroundColor: overdue ? 'rgba(211, 47, 47, 0.08)' : dueSoon ? 'rgba(237, 108, 2, 0.08)' : 'inherit',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: overdue ? 'rgba(211, 47, 47, 0.15)' : dueSoon ? 'rgba(237, 108, 2, 0.15)' : 'rgba(29, 68, 52, 0.04)',
                        transform: 'scale(1.001)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: overdue ? 'rgba(211, 47, 47, 0.05)' : dueSoon ? 'rgba(237, 108, 2, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                      },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary', py: 2 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkGreenColor }}>
                        {decision.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {(() => {
                        const latestComment = getLatestComment(decision.progressCommentsHistory);
                        const commentCount = decision.progressCommentsHistory?.length || 0;
                        
                        if (commentCount === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No comments
                            </Typography>
                          );
                        }
                        
                        return (
                          <Tooltip 
                            title={latestComment || 'No comment'} 
                            arrow
                            placement="top"
                          >
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'help',
                                color: 'text.secondary'
                              }}
                            >
                              {truncateText(latestComment, 40)}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={decision.status || 'Pending'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          height: '28px',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        color={getStatusColor(decision.status)}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {decision.ownedBy || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {decision.dueDate ? (
                        <Chip
                          label={format(new Date(decision.dueDate), 'MMM dd, yyyy')}
                          size="small"
                          sx={{
                            fontWeight: overdue || dueSoon ? 600 : 400,
                            height: '28px',
                            fontSize: '0.75rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                          color={overdue ? 'error' : dueSoon ? 'warning' : 'default'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {decision.updatedAt ? format(new Date(decision.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}
                      </Typography>
                    </TableCell>
                    {isAuthenticated() && (
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="View Full Decision" arrow>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setViewingDecision(decision);
                                setViewDialogOpen(true);
                              }}
                              sx={{
                                color: 'info.main',
                                '&:hover': {
                                  backgroundColor: 'info.light',
                                  color: 'info.dark',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(decision)}
                              sx={{
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.dark',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteDecision(decision._id)}
                              disabled={deleting === decision._id}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                  transform: 'scale(1.1)'
                                },
                                '&:disabled': {
                                  opacity: 0.5
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {deleting === decision._id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Full Decision Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Decision Details
        </DialogTitle>
        <DialogContent>
          {viewingDecision && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Title
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: darkGreenColor }}>
                    {viewingDecision.title}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingDecision.description || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={viewingDecision.status || 'Pending'}
                    size="small"
                    color={getStatusColor(viewingDecision.status)}
                    sx={{ fontWeight: 600 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Owned By
                  </Typography>
                  <Typography variant="body1">
                    {viewingDecision.ownedBy || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {viewingDecision.dueDate 
                      ? format(new Date(viewingDecision.dueDate), 'MMM dd, yyyy')
                      : '-'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Decision Date
                  </Typography>
                  <Typography variant="body1">
                    {viewingDecision.decisionDate 
                      ? format(new Date(viewingDecision.decisionDate), 'MMM dd, yyyy')
                      : '-'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Changed By
                  </Typography>
                  <Typography variant="body1">
                    {viewingDecision.updatedBy?.username || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Changed Date
                  </Typography>
                  <Typography variant="body1">
                    {viewingDecision.updatedAt 
                      ? format(new Date(viewingDecision.updatedAt), 'MMM dd, yyyy HH:mm')
                      : '-'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Progress Comments
                  </Typography>
                  {viewingDecision.progressCommentsHistory && viewingDecision.progressCommentsHistory.length > 0 ? (
                    <List dense>
                      {viewingDecision.progressCommentsHistory
                        .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
                        .map((comment, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={comment.comment}
                              secondary={
                                comment.addedAt 
                                  ? format(new Date(comment.addedAt), 'MMM dd, yyyy HH:mm')
                                  : ''
                              }
                              primaryTypographyProps={{ sx: { whiteSpace: 'pre-wrap' } }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No progress comments
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Final Decision
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingDecision.finalDecisionComment || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDecision ? 'Edit Decision' : 'Add Decision'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Progress Comments History
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" gap={1} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add a progress comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddCommentIcon />}
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                    >
                      Add
                    </Button>
                  </Box>
                  {formData.progressCommentsHistory.length > 0 && (
                    <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      {formData.progressCommentsHistory.map((comment, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveComment(index)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={comment.comment}
                            secondary={comment.addedAt ? format(new Date(comment.addedAt), 'MMM dd, yyyy HH:mm') : 'Just now'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Final Decision Comment"
                  value={formData.finalDecisionComment}
                  onChange={(e) => setFormData({ ...formData, finalDecisionComment: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                    <MenuItem value="On Hold">On Hold</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Owned By"
                  value={formData.ownedBy}
                  onChange={(e) => setFormData({ ...formData, ownedBy: e.target.value })}
                  placeholder="Enter owner name"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Decision Date"
                  type="date"
                  value={formData.decisionDate}
                  onChange={(e) => setFormData({ ...formData, decisionDate: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Date when the decision was made (not shown in table)"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveDecision}
            variant="contained"
            disabled={saving || !formData.title.trim()}
            sx={{
              backgroundColor: darkGreenColor,
              '&:hover': { backgroundColor: '#0f2e1f' }
            }}
          >
            {saving ? 'Saving...' : editingDecision ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HipaaCuiDecisions;


